import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  BROWSE_WITHOUT_AN_ACCOUNT,
  JOIN_LABEL,
  SIGN_IN_LABEL,
  WELCOME_HEADLINE,
  WELCOME_SUBHEAD,
} from './welcome-constants';

/**
 * The member app's front door for a visitor with no session (`MEM-046`).
 *
 * ## Transcribed from `Member App.dc.html`, the WELCOME screen
 *
 * The app's initial state in the prototype: the full canvas on `--spark-gradient`, white
 * throughout, `64px 28px 40px` of padding, and three groups spaced apart by `justify-between` —
 * the mark at the top, the wordmark and the two lines of copy in the middle, the two actions at
 * the bottom. The primary action is a white pill at `--radius-pill` with `--shadow-lg` in
 * `--brand-primary` text; the secondary is bare white text.
 *
 * ## The mark and the wordmark are the prototype's own files (corrected 2026-09-12)
 *
 * They were drawn rather than fetched, on the stated grounds that *"the prototype uses
 * `icon-white.png` and `logotype-white.png`, neither of which exists in this repository"*. **Both
 * do**, and have since the member chrome was built: `public/brand/icon-white.png` and
 * `public/brand/logotype-white.png` are the 10 September bundle's own files, byte for byte. The
 * sentence was true of `public/icon.svg`, which is the gradient app icon and would indeed vanish
 * against this background, and it was carried from there to the two files beside it.
 *
 * What stood in their place was a white **H** in a translucent rounded square — which is the same
 * thing the parity reports have twice named on the sign-in screen, in a different colour: a
 * letter in a box where the product's mark belongs. It is the first thing a stranger following an
 * organizer's link sees.
 *
 * The sizes are the prototype's: `icon-white.png` at 150px and `logotype-white.png` at 28px
 * (`Member App.dc.html`, the WELCOME screen, l.51–53). `alt=""` on the mark because the wordmark
 * below it is the same name and carries it.
 *
 * ## Phones only (2026-09-24)
 *
 * From tablet width up the hero is not shown and a visitor lands on Discover's event list, as a
 * member does (`MEM-046` AC-7). The 11 September Web App design, which governs every width, sends
 * "Get started", "Browse all events" and the header's Events straight to the list, and with the
 * hero in front of it those links landed on a purple panel instead (TW 44175337, 44175300,
 * 44175302). Sumit chose to keep it on the phone, where it is the app's own opening screen:
 * *"Welcome screen is fine on phone, but on web we can show event listing."*
 *
 * ## What this is not
 *
 * **Not a wall.** `MEM-019` AC-1 keeps the Discover list, its search and its atmosphere filter
 * available with no account, so this hero sits *above* Discover on the same URL rather than in
 * front of it. It fills the visible canvas — a front door should be the whole of what you first
 * see — and then says, in {@link BROWSE_WITHOUT_AN_ACCOUNT}, that there is more below and that
 * it is not gated. Building the prototype's wall as drawn would have revoked a passing
 * acceptance criterion silently, because a wall looks finished: nothing fails, the events are
 * just gone for everybody without an account.
 *
 * **Not remembered.** There is no "seen it once" flag in local storage. `MEM-042` AC-1 puts the
 * source of truth on the server, and whether somebody has seen the welcome screen is exactly the
 * device-local state that makes a phone and a laptop disagree about what the product is. The
 * condition is the session and nothing else.
 */
export function WelcomeHero(): React.ReactElement {
  return (
    <section
      aria-labelledby="welcome-headline"
      // Phone shell only (`MEM-046` AC-7): `md:hidden` takes it away from tablet width up, where
      // `/app` opens straight on Discover. CSS rather than a media-query hook, so the first frame
      // is right at every width.
      //
      // The negative margin escapes the phone shell's `px-4` / `pt-6` content padding so the
      // gradient runs to the edges of the canvas. Get it wrong and a strip of page background
      // shows down each side of the hero.
      //
      // The height is the visible canvas: the phone's tab bar is fixed and overlays the bottom,
      // and its 52px brand bar (`phone-top-bar.tsx`) sits above the safe area at the top. Without
      // subtracting both, the two actions sit under the chrome.
      className="-mx-4 -mt-6 flex min-h-[calc(100svh-4.5rem-52px-env(safe-area-inset-top)-env(safe-area-inset-bottom))] flex-col justify-between bg-spark px-7 pb-10 pt-16 text-white md:hidden"
    >
      {/* 1 — the mark, at the prototype's 150px. */}
      <div className="flex justify-center">
        <img src="/brand/icon-white.png" alt="" className="h-[150px] w-auto" />
      </div>

      {/* 2 — the wordmark and the promise. Centred in a readable column rather than run to the
          full width of the canvas: a headline set edge to edge is a banner, not a
          sentence, and the subhead below it would be a single thin line. */}
      <div className="mx-auto w-full max-w-xl text-center">
        <img
          src="/brand/logotype-white.png"
          alt="H2HSpark"
          className="mx-auto h-7 w-auto"
        />
        <h1 id="welcome-headline" className="mt-5 text-3xl leading-[1.12] text-white">
          {WELCOME_HEADLINE}
        </h1>
        <p className="mt-3 text-base text-white/90">{WELCOME_SUBHEAD}</p>
      </div>

      {/* 3 — the way in. Links rather than buttons: both of these navigate, so a member can open
          either in a new tab and a screen reader announces them for what they are. */}
      <div className="mx-auto flex w-full max-w-sm flex-col gap-3">
        <Link
          to="/app/join"
          className="rounded-pill bg-white px-4 py-4 text-center font-brand text-base font-bold text-brand shadow-lg transition-transform hover:scale-[1.01] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white motion-reduce:transition-none motion-reduce:hover:scale-100"
        >
          {JOIN_LABEL}
        </Link>
        <Link
          to="/app/login"
          className="rounded-pill px-4 py-2 text-center font-brand text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {SIGN_IN_LABEL}
        </Link>

        {/* `MEM-046` AC-5. Not a control — what is below is reached by scrolling, and a link that
            scrolled the page would be a second way to do what the thumb already does. It is a
            label, and its job is to say the scroll is worth taking. */}
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-white/85">
          {BROWSE_WITHOUT_AN_ACCOUNT}
          <ChevronDown className="h-4 w-4" aria-hidden />
        </p>
      </div>
    </section>
  );
}
